// Third-party imports
import { HttpError } from 'react-admin';

// Local imports - types
import type {
  SearchFacet,
  CatalogItem,
  CatalogItemsDataProvider,
  ParentSkuDetails
} from '@src/types';

// Local imports - shared utilities
import { requireAuth, requirePartner, parseImageUrls } from '../shared';
import { DashboardCurrency } from '@src/utils/valueFormatters';

// Local imports - API
import * as RewareApi from '@src/apis/reware';

/* There are some "any" assertions in return data to satisfy the generic approach to using the React Admin dataProvider. Without these,
React Admin will try to warn us that a dataProvider method could be called with another resource method. When calling methods explicitly,
use type parameters for type safety:
dataProvider.getList<CatalogItem>(DashboardResources.CatalogItems, params) returns Promise<GetListResult<CatalogItem>>
*/
export const catalogItemsDataProvider: CatalogItemsDataProvider = {
  getList: async (resource, params) => {
    try {
      const currentPartner = requirePartner();
      const { page = 1, perPage = 24 } = params.pagination || {};
      const { searchQuery: query = '' } = params.filter || {};

      // Convert react-admin filters to catalog API filters
      const filters = Object.entries(params.filter || {})
        .filter(([key, value]) => key !== 'searchQuery' && value)
        .map(([tag, name]) => ({ tag, name: name as string }));

      const searchParams = {
        partnerCode: currentPartner.code,
        query,
        filters,
        offset: (page - 1) * perPage,
        limit: perPage,
      };

      const token = requireAuth();

      // Fetch both catalog items and search facets in parallel
      const [catalogResult, facetsResult] = await Promise.allSettled([
        RewareApi.searchStyle(searchParams, token),
        RewareApi.getSearchFacets({
          partnerCode: currentPartner.code,
          token,
          query,
          filters,
        }),
      ]);

      // Check if catalog fetch failed (primary data - must succeed)
      if (catalogResult.status === 'rejected') {
        if (catalogResult.reason instanceof HttpError) throw catalogResult.reason;
        const message = catalogResult.reason instanceof Error ? catalogResult.reason.message : catalogResult.reason;
        throw new HttpError(message, 500);
      }

      const response = catalogResult.value;

      // Handle facets fetch failure (supplementary data - graceful degradation)
      let facetsResponse: SearchFacet[] = [];
      if (facetsResult.status === 'rejected') {
        console.error('CatalogItemsDataProvider.getList - facets fetch failed:', {
          resource,
          params,
          error: facetsResult.reason instanceof Error ? facetsResult.reason.message : facetsResult.reason,
        });
      } else {
        facetsResponse = facetsResult.value.searchFacets || [];
      }

      // Handle null response - means no catalog items found
      if (!response || !response.search || !response.search.items) {
        return {
          data: [],
          total: 0,
          meta: {
            filters: facetsResponse,
          },
        };
      }

      const catalogItems: CatalogItem[] = response.search.items.map((item) => {
        const { imageUrls, mainImageUrl } = parseImageUrls(item.imageUrls);

        return {
          id: item.parentSKU,
          parentSku: item.parentSKU,
          title: item.title,
          brand: item.brand,
          brandDisplay: item.brandDisplay,
          category: item.category,
          categories: item.categories,
          department: item.department,
          imageUrls,
          mainImageUrl,
          msrpPrice: DashboardCurrency.fromMinorUnit(item.msrpPrice),
          variantCount: item.variantCount,
          colorCode: item.colorCode,
          colorDisplay: item.colorDisplay,
        };
      });

      return {
        // eslint-disable-next-line
        data: catalogItems as any, // see dataProvider comment
        total: response.search.count || 0,
        meta: {
          filters: facetsResponse,
        },
      };
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw and it will log automatically
      // This is additional detail logging
      console.error('CatalogItemsDataProvider.getList failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to load catalog items.', status);
    }
  },

  getOne: async (resource, params) => {
    try {
      const currentPartner = requirePartner();
      const token = requireAuth();

      // Fetch both item details and attribute data in parallel
      const [itemResponse, attributeResponse] = await Promise.all([
        RewareApi.getParentSkuDetails({
          parentSku: params.id as string,
          partnerCode: currentPartner.code,
          token
        }),
        RewareApi.catalogAttributes(currentPartner.code, token)
      ]);

      const catalogItem: ParentSkuDetails = {
        id: params.id as string,
        ...itemResponse,
        msrpPrice: DashboardCurrency.fromMinorUnit(itemResponse.msrpPrice),
        // Include attribute data as part of the catalog item
        attributeData: attributeResponse,
      };
      // eslint-disable-next-line
      return { data: catalogItem as any }; // see dataProvider comment
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw and it will log automatically
      // This is additional detail logging
      console.error('CatalogItemsDataProvider.getOne failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to load catalog item details.', status);
    }
  },

  getMany: async (resource, params) => {
    // Not needed for current catalog functionality
    throw new Error('getMany not implemented');
  },

  getManyReference: async (resource, params) => {
    // Not needed for current catalog functionality
    throw new Error('getManyReference not implemented');
  },

  create: async (resource, params) => {
    // Not needed for current catalog functionality (read-only for now)
    throw new Error('Create not implemented');
  },

  update: async (resource, params) => {
    try {
      const currentPartner = requirePartner();
      const token = requireAuth();

      const { id, ...updateData } = params.data;

      // Transform the form data to match API expectations
      const detailsData = {
        availability: updateData.availability || [],
        makeAvailabilitiesConsistent: updateData.makeAvailabilitiesConsistent,
        brandDisplay: updateData.brandDisplay || '',
        categories: updateData.categories || [],
        departments: updateData.departments || [],
        gender: updateData.gender?.code
          ? {
              code: updateData.gender.code,
              name: updateData.attributeData?.validGenders?.find(
                (g: { code: string; name: string }) => g.code === updateData.gender.code,
              )?.name ?? updateData.gender.code,
            }
          : { code: '', name: '' },
        msrpPrice: DashboardCurrency.toMinorUnit(updateData.msrpPrice || 0),
        parentSku: id as string,
        partnerCategory: updateData.partnerCategory || '',
        partnerCode: currentPartner.code,
        productDescription: updateData.productDescription || '',
        productName: updateData.productName || '',
        rubricCode: updateData.rubricCode || '',
        taxCode: updateData.taxCode || '',
        token,
      };

      // Only send image fields that were actually modified to avoid the backend
      // clearing one type when only the other was changed.
      const partnerDirty = !!updateData.partnerImagesDirty;
      const studioDirty = !!updateData.studioImagesDirty;
      const hasImageChanges = partnerDirty || studioDirty;

      // Update details and images in parallel when both are needed
      const [detailsResponse, imagesResponse] = await Promise.all([
        RewareApi.updateParentSkuDetails(detailsData),
        hasImageChanges ? RewareApi.updateParentSkuImages({
          parentSku: id as string,
          partnerCode: currentPartner.code,
          token,
          imagesByColorway: partnerDirty ? updateData.imagesByColorway : undefined,
          studioImagesByColorway: studioDirty ? updateData.studioImagesByColorway : undefined,
          files: partnerDirty ? updateData.files : undefined,
          studioFiles: studioDirty ? updateData.studioFiles : undefined,
        }) : undefined,
      ]);

      // Use the backend response as the source of truth for image state.
      // Backend handles all cascading logic and returns the correct scope key structure.
      const updatedItem: ParentSkuDetails = {
        id: id,
        ...detailsResponse,
        msrpPrice: DashboardCurrency.fromMinorUnit(detailsResponse.msrpPrice),
        ...(imagesResponse && partnerDirty && {
          imagesByColorway: imagesResponse.imagesByColorway,
        }),
        ...(imagesResponse && studioDirty && {
          studioImagesByColorway: imagesResponse.studioImagesByColorway,
        }),
      };

      // eslint-disable-next-line
      return { data: updatedItem as any }; // see dataProvider comment
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw and it will log automatically
      // This is additional detail logging
      console.error('CatalogItemsDataProvider.update failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to update catalog item.', status);
    }
  },

  updateMany: async (resource, params) => {
    // Not needed for current catalog functionality
    throw new Error('UpdateMany not implemented');
  },

  delete: async (resource, params) => {
    // Not needed for current catalog functionality
    throw new Error('Delete not implemented');
  },

  deleteMany: async (resource, params) => {
    // Not needed for current catalog functionality
    throw new Error('DeleteMany not implemented');
  },
};

export default catalogItemsDataProvider;
